/**
 * This file will provide all the getters for each calculator to extract the expected values from redux
 */
import _ from 'lodash';

import { LOAN_TYPE_FHA, LOAN_TYPE_VA } from '@nerdwallet/home-constants';
import {
  MORTGAGE,
  HOME_AFFORDABILITY,
  CALCULATOR_REDUCER_NAME_HASH,
} from '../../lib/analytics-constants';
import type { Calculator } from '../../lib/analytics-constants';

import {
  PRINCIPAL_AND_INTEREST,
  PROPERTY_TAXES,
  HOMEOWNERS_ASSOCIATION_FEES,
  HOMEOWNERS_INSURANCE,
  PRIVATE_MORTGAGE_INSURANCE,
  MORTGAGE_PAYMENT,
  CALCULATOR_FIELDS,
  DOWN_PAYMENT,
  TOTAL_CLOSING_COST,
  MORTGAGE_INSURANCE,
} from './analytics-constants';
import type { FieldName } from './types';

const MORTGAGE_VALUE_PATH_HASH = {
  [PRINCIPAL_AND_INTEREST]: 'outputs.monthlyPayments.mortgage',
  [PROPERTY_TAXES]: 'overrides.monthlyPayments.propertyTax',
  [HOMEOWNERS_INSURANCE]: 'overrides.monthlyPayments.homeownersInsurance',
  [HOMEOWNERS_ASSOCIATION_FEES]: 'overrides.monthlyPayments.hoa',
  [PRIVATE_MORTGAGE_INSURANCE]:
    'overrides.monthlyPayments.mortgageInsurancePayment',
};

const MORTGAGE_PLACEHOLDER_PATH_HASH = {
  [PRINCIPAL_AND_INTEREST]: 'outputs.monthlyPayments.mortgage',
  [PROPERTY_TAXES]: 'outputs.monthlyPayments.propertyTax',
  [HOMEOWNERS_INSURANCE]: 'outputs.monthlyPayments.homeownersInsurance',
  [HOMEOWNERS_ASSOCIATION_FEES]: 'outputs.monthlyPayments.hoa',
  [PRIVATE_MORTGAGE_INSURANCE]:
    'outputs.monthlyPayments.mortgageInsurancePayment',
};

const HOME_AFFORDABILITY_VALUE_PATH_HASH = {
  [MORTGAGE_PAYMENT]: 'adjustedOutputs.monthlyPayments.mortgage',
  [PROPERTY_TAXES]: 'overrides.monthlyPayments.propertyTax',
  [HOMEOWNERS_INSURANCE]: 'overrides.monthlyPayments.homeownersInsurance',
  [HOMEOWNERS_ASSOCIATION_FEES]: 'overrides.monthlyPayments.hoa',
  [MORTGAGE_INSURANCE]: 'overrides.monthlyPayments.mortgageInsurancePayment',
  [PRIVATE_MORTGAGE_INSURANCE]:
    'overrides.monthlyPayments.mortgageInsurancePayment',
  [DOWN_PAYMENT]: 'adjustedOutputs.downPayment',
  [TOTAL_CLOSING_COST]: 'adjustedOutputs.closingCosts.total',
};

const HOME_AFFORDABILITY_PLACEHOLDER_PATH_HASH = {
  [MORTGAGE_PAYMENT]: 'adjustedOutputs.monthlyPayments.mortgage',
  [PROPERTY_TAXES]: 'adjustedOutputs.monthlyPayments.propertyTax',
  [HOMEOWNERS_INSURANCE]: 'adjustedOutputs.monthlyPayments.homeownersInsurance',
  [HOMEOWNERS_ASSOCIATION_FEES]: 'adjustedOutputs.monthlyPayments.hoa',
  [PRIVATE_MORTGAGE_INSURANCE]:
    'adjustedOutputs.monthlyPayments.mortgageInsurancePayment',
  [MORTGAGE_INSURANCE]:
    'adjustedOutputs.monthlyPayments.mortgageInsurancePayment',
  [DOWN_PAYMENT]: 'adjustedOutputs.downPayment',
  [TOTAL_CLOSING_COST]: 'adjustedOutputs.closingCosts.total',
};

const CALCULATORS_VALUE_HASH = {
  [MORTGAGE]: MORTGAGE_VALUE_PATH_HASH,
  [HOME_AFFORDABILITY]: HOME_AFFORDABILITY_VALUE_PATH_HASH,
};

const CALCULATORS_PLACEHOLDER_HASH = {
  [MORTGAGE]: MORTGAGE_PLACEHOLDER_PATH_HASH,
  [HOME_AFFORDABILITY]: HOME_AFFORDABILITY_PLACEHOLDER_PATH_HASH,
};

/**
 *
 * @param store - redux store
 * @param calculator - calculator where we want to retrieve the value
 * @param fieldName -
 * @returns field value
 */
export const getFieldValueByCalculator = (
  store: any,
  calculator: Calculator,
  fieldName: FieldName
): number => {
  const reducer = store?.[CALCULATOR_REDUCER_NAME_HASH[calculator]];
  const valuePath = CALCULATORS_VALUE_HASH[calculator][fieldName];

  return _.get(reducer, valuePath);
};

/**
 *
 * @param store - redux store
 * @param calculator - calculator where we want to retrieve the placeholder
 * @param fieldName -
 * @returns field placeholder value
 */
export const getFieldPlaceholderByCalculator = (
  store: any,
  calculator: Calculator,
  fieldName: FieldName
): number => {
  const reducer = store?.[CALCULATOR_REDUCER_NAME_HASH[calculator]];
  const placeholderPath = CALCULATORS_PLACEHOLDER_HASH[calculator][fieldName];

  return _.get(reducer, placeholderPath);
};

/**
 * This util is only used inside `bar-chart` component. To retrieve the max value for the specified calculator
 * as different calculators show different fields
 *
 * @param store - redux store
 * @param calculator - from which calculator we want the max value
 * @returns max value
 */
export const getMaxByCalculator = (
  store: any,
  calculator: Calculator
): number => {
  const fieldsToCheck = CALCULATOR_FIELDS[calculator];
  const values = fieldsToCheck.map((field: FieldName) => {
    // if for any reason any value is undefined we will take it as 0
    const placeholder = getFieldPlaceholderByCalculator(
      store,
      calculator,
      field
    );
    const value = getFieldValueByCalculator(store, calculator, field);

    return value ?? placeholder ?? 0;
  });

  return Math.max(...values, 0);
};

/**
 * This function returns the array of field names expected to show for the specified calculator
 * And also if it's the control-panel view or not
 *
 * @param calculator - which calculator we are rendering MonthlyPayment
 * @param view - used to determine which Fields to show within the same calc
 * @returns FieldName[]
 */
export const getFieldNames = (
  calculator: Calculator,
  view?: 'ControlPanel' | 'DownPayments'
): FieldName[] => {
  const key = `${calculator}${view || ''}`;

  return CALCULATOR_FIELDS[key];
};

/**
 * This util wether we should show PrivateMortgageInsurance or not
 * @param store - store redux store
 * @param calculator - from which calculator we want to check (mortgage | homeAffordability)
 */
export const showPMI = (store: any, calculator: Calculator): boolean => {
  const EXPECTED_CALCULATORS_TO_SHOW_PMI = [MORTGAGE, HOME_AFFORDABILITY];
  const reducer = store?.[CALCULATOR_REDUCER_NAME_HASH[calculator]];
  const loanType = _.get(reducer, 'adjustedOutputs.loanType');

  if (!EXPECTED_CALCULATORS_TO_SHOW_PMI.includes(calculator)) return false;

  if (calculator === HOME_AFFORDABILITY && loanType === LOAN_TYPE_VA) {
    return false;
  }
  let pmiValue = null;

  if (calculator === HOME_AFFORDABILITY) {
    pmiValue = !!(
      loanType !== LOAN_TYPE_FHA &&
      (_.get(reducer, 'adjustedOutputs.isDownPaymentBelowMin') ||
        _.get(reducer, 'overrides.monthlyPayments.mortgageInsurancePayment'))
    );
  } else {
    // for some reason we check the placeholder value to see if we need to show it or not
    pmiValue = getFieldPlaceholderByCalculator(
      store,
      calculator,
      'privateMortgageInsurance'
    );
  }

  return pmiValue >= 0;
};

/**
 * This util wether we should show MortgageInsurance or not
 * @param store - store redux store
 * @param calculator - from which calculator we want to check (homeAffordability)
 */
export const showMortgageInsurance = (
  store: any,
  calculator: Calculator
): boolean => {
  const EXPECTED_CALCULATORS_TO_SHOW_PMI = [HOME_AFFORDABILITY];
  const reducer = store?.[CALCULATOR_REDUCER_NAME_HASH[calculator]];
  const loanType = _.get(reducer, 'adjustedOutputs.loanType');

  if (!EXPECTED_CALCULATORS_TO_SHOW_PMI.includes(calculator)) return false;

  return !!(
    loanType === LOAN_TYPE_FHA &&
    (_.get(reducer, 'adjustedOutputs.isDownPaymentBelowMin') ||
      _.get(reducer, 'overrides.monthlyPayments.mortgageInsurancePayment'))
  );
};

/**
 * This util wether we should show insufficient disclaimer or not
 * @param store - store redux store
 * @param calculator - from which calculator we want to check (homeAffordability)
 */
export const showInsufficientDisclaimer = (
  store: any,
  calculator: Calculator
): boolean => {
  const EXPECTED_CALCULATORS_TO_SHOW_PMI = [HOME_AFFORDABILITY];
  const reducer = store?.[CALCULATOR_REDUCER_NAME_HASH[calculator]];
  const fundsNeeded = _.get(reducer, 'adjustedOutputs.fundsNeeded');
  const fundsNeededOverride = _.get(reducer, 'overrides.fundsNeeded');
  const loanType = _.get(reducer, 'adjustedOutputs.loanType');

  if (!EXPECTED_CALCULATORS_TO_SHOW_PMI.includes(calculator)) return false;

  return (
    loanType === LOAN_TYPE_VA &&
    fundsNeededOverride !== undefined &&
    fundsNeeded > fundsNeededOverride
  );
};

/**
 * This util wether we should show minimum disclaimer or not
 * @param store - store redux store
 * @param calculator - from which calculator we want to check (homeAffordability)
 */
export const showMinimumDisclaimer = (
  store: any,
  calculator: Calculator
): boolean => {
  const EXPECTED_CALCULATORS_TO_SHOW_PMI = [HOME_AFFORDABILITY];
  const reducer = store?.[CALCULATOR_REDUCER_NAME_HASH[calculator]];
  const fundsNeeded = _.get(reducer, 'adjustedOutputs.fundsNeeded');
  const fundsNeededOverride = _.get(reducer, 'overrides.fundsNeeded');
  const loanType = _.get(reducer, 'adjustedOutputs.loanType');

  if (!EXPECTED_CALCULATORS_TO_SHOW_PMI.includes(calculator)) return false;

  return (
    loanType !== LOAN_TYPE_VA &&
    fundsNeededOverride !== undefined &&
    fundsNeeded > fundsNeededOverride
  );
};
